/**
 * F-144 (slice 3a, team thread 2026-10-13) — "What's changing" on an update-request line.
 *
 * Candace: a box per thing that can change (SKU / UIN / Name / Build / Artwork — Andrew added
 * Price) with a note of what exactly changes, set at Triage; it is what PIQ QP / PIM / Web read
 * from the ClickUp card today. Diane: only the items marked here are changes downstream —
 * correcting a value while reviewing the build is not a change.
 *
 * The form holds all six boxes (on + note); the wire / stored list holds the MARKED items only
 * (API DdChangeIntents: known keys, fixed order, notes ≤ 2000 chars).
 */
import type { ModifyRequest } from '@/lib/dd-queue/types'
import type { ChangeIntent, ChangeIntentKey, PimRecord } from '@/lib/dd-queue/types/subjectMutation'
import { changeDetailRows, currentBuild } from '@/lib/dd-queue/changeRequestDetails'

export const CHANGE_INTENT_KEYS: readonly ChangeIntentKey[] = [
  'sku',
  'uin',
  'name',
  'build',
  'artwork',
  'price'
]

export const CHANGE_INTENT_LABELS: Record<ChangeIntentKey, string> = {
  sku: 'SKU',
  uin: 'UIN',
  name: 'Name',
  build: 'Build',
  artwork: 'Artwork',
  price: 'Price'
}

export const CHANGE_INTENT_NOTE_MAX = 2000

export type ChangeIntentForm = Record<ChangeIntentKey, { on: boolean; note: string }>

/**
 * Which box a request's change type pre-ticks. `web` ticks Name only when it carries a title
 * (a listing description / thumbnail has no box of its own); SKU is never pre-ticked — Triage
 * marks it when the change needs a new SKU. Discontinue / Standard-Custom / section notes
 * stay on the Change Request card.
 */
const INTENT_FOR_TYPE: Partial<Record<string, ChangeIntentKey>> = {
  price: 'price',
  uin: 'uin',
  web: 'name',
  spec: 'build',
  sb2: 'build',
  sb3: 'build',
  sb4: 'build',
  sb5: 'build',
  parts: 'build',
  bundle_edit: 'build',
  design: 'artwork',
  artwork: 'artwork'
}

function isKey(value: unknown): value is ChangeIntentKey {
  return typeof value === 'string' && (CHANGE_INTENT_KEYS as readonly string[]).includes(value)
}

export function emptyChangeIntentForm(): ChangeIntentForm {
  return {
    sku: { on: false, note: '' },
    uin: { on: false, note: '' },
    name: { on: false, note: '' },
    build: { on: false, note: '' },
    artwork: { on: false, note: '' },
    price: { on: false, note: '' }
  }
}

/**
 * The form a fresh Triage opens on: boxes ticked from the request's change types, notes
 * pre-filled with what the Change Request card prints — `current → new` where the product's
 * PIM record (or the form's snapshot) knows the current value, plain rows otherwise; files,
 * thumbnails and the form's "Action" rows are left out.
 */
export function prefillChangeIntents(
  modify: ModifyRequest,
  pimRecord?: PimRecord | null
): ChangeIntentForm {
  const form = emptyChangeIntentForm()
  const current = currentBuild(pimRecord, modify.product)
  for (const type of modify.changeTypes) {
    const key = INTENT_FOR_TYPE[type]
    if (!key) continue
    if (type === 'web') {
      const web = modify.changes.web as { title?: unknown } | undefined
      if (typeof web?.title !== 'string' || web.title.trim() === '') continue
    }
    const parts: string[] = []
    for (const row of changeDetailRows(type, modify.changes, modify.product, current)) {
      if (row.kind || row.term === 'Action') continue
      if (type === 'web' && row.term !== 'Title') continue
      parts.push(
        row.current !== undefined
          ? `${row.term} ${row.current ?? '—'} → ${row.detail}`
          : `${row.term}: ${row.detail}`
      )
    }
    const box = form[key]
    box.on = true
    const text = parts.join(' · ')
    if (text) box.note = box.note ? `${box.note} · ${text}` : text
  }
  return form
}

/** The form for a line Triage already saved (a line sent back to Triage re-opens on it). */
export function changeIntentFormFromStored(stored: ChangeIntent[]): ChangeIntentForm {
  const form = emptyChangeIntentForm()
  for (const item of stored) {
    if (!isKey(item.key)) continue
    form[item.key] = { on: true, note: typeof item.note === 'string' ? item.note : '' }
  }
  return form
}

/** The wire / stored list: marked boxes only, in the fixed order, notes trimmed. */
export function changeIntentsToWire(form: ChangeIntentForm): ChangeIntent[] {
  const out: ChangeIntent[] = []
  for (const key of CHANGE_INTENT_KEYS) {
    const box = form[key]
    if (!box?.on) continue
    out.push({ key, note: (box.note ?? '').trim().slice(0, CHANGE_INTENT_NOTE_MAX) })
  }
  return out
}

/** An API list → the SPA's (unknown keys dropped, notes as text, fixed order). */
export function changeIntentsFromApi(raw: unknown): ChangeIntent[] {
  if (!Array.isArray(raw)) return []
  const byKey = new Map<ChangeIntentKey, string>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const key = (item as { key?: unknown }).key
    if (!isKey(key)) continue
    const note = (item as { note?: unknown }).note
    byKey.set(key, typeof note === 'string' ? note : '')
  }
  return CHANGE_INTENT_KEYS.filter((k) => byKey.has(k)).map((key) => ({
    key,
    note: byKey.get(key)!
  }))
}
